import React, { useEffect, useRef, useState } from 'react';
import { Button, DatePicker, Input, message, Modal, Space, Typography } from 'antd';
import { CopyOutlined, DownloadOutlined, LinkOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { createVersionShareLink } from '../services/api';
import { getProductDisplayName } from '../utils/format';

const { Text } = Typography;

const getDefaultExpiresAt = () => dayjs().add(1, 'hour').second(0).millisecond(0);

const disabledExpiryTime = (current) => {
  if (!current || !current.isSame(dayjs(), 'day')) return {};
  const now = dayjs();
  return {
    disabledHours: () => Array.from({ length: now.hour() }, (_, hour) => hour),
    disabledMinutes: (selectedHour) => (
      selectedHour === now.hour()
        ? Array.from({ length: now.minute() + 1 }, (_, minute) => minute)
        : []
    ),
  };
};

function copyTextFromInput(inputElement) {
  if (!inputElement) return false;
  inputElement.focus({ preventScroll: true });
  inputElement.select();
  inputElement.setSelectionRange(0, inputElement.value.length);

  try {
    return document.execCommand('copy');
  } catch {
    return false;
  }
}

function copyTextFromHiddenTextarea(value) {
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);

  try {
    return copyTextFromInput(textarea);
  } finally {
    document.body.removeChild(textarea);
  }
}

async function copyShareLinkToClipboard(text, inputElement) {
  const value = String(text || '');
  if (!value) return false;

  if (copyTextFromInput(inputElement)) return true;
  if (copyTextFromHiddenTextarea(value)) return true;

  if (window.isSecureContext && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      return false;
    }
  }

  return false;
}

export default function VersionShareLinkModal({ open, version, onClose }) {
  const shareLinkInputRef = useRef(null);
  const [expiresAt, setExpiresAt] = useState(getDefaultExpiresAt());
  const [shareLink, setShareLink] = useState('');
  const [generatedExpiresAt, setGeneratedExpiresAt] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setExpiresAt(getDefaultExpiresAt());
    setShareLink('');
    setGeneratedExpiresAt('');
    setCreating(false);
  }, [open, version?.id]);

  const versionLabel = version
    ? `${getProductDisplayName(version.product_model) || version.product_name || ''} ${version.version ? `v${version.version}` : ''}`.trim()
    : '';
  const canCreate = Boolean(version?.id && expiresAt && expiresAt.isAfter(dayjs()));

  const handleExpiryChange = (value) => {
    const normalizedValue = value ? value.second(0).millisecond(0) : null;
    setExpiresAt(normalizedValue);
    setShareLink('');
    setGeneratedExpiresAt('');
  };

  const handleCreate = async () => {
    if (!canCreate) {
      message.warning('请选择有效的过期时间');
      return;
    }
    setCreating(true);
    try {
      const res = await createVersionShareLink(version.id, {
        expires_at: expiresAt.toISOString(),
      });
      const data = res.data?.data || {};
      setShareLink(data.url || '');
      setGeneratedExpiresAt(data.expires_at || expiresAt.toISOString());
      message.success('分享下载链接已生成');
    } catch (err) {
      message.error(err.response?.data?.message || '生成分享下载链接失败');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!shareLink) return;
    try {
      const copied = await copyShareLinkToClipboard(shareLink, shareLinkInputRef.current?.input);
      if (copied) {
        message.success('链接已复制');
        return;
      }
      throw new Error('copy failed');
    } catch {
      message.error('复制失败，请手动选中链接复制');
    }
  };

  return (
    <Modal
      title={versionLabel ? `分享下载 - ${versionLabel}` : '分享下载'}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>关闭</Button>,
        <Button
          key="create"
          type="primary"
          icon={<LinkOutlined />}
          loading={creating}
          disabled={!canCreate}
          onClick={handleCreate}
        >
          生成链接
        </Button>,
      ]}
      destroyOnHidden
      width={560}
    >
      <Space direction="vertical" size={12} className="version-share-modal">
        <div className="version-share-field">
          <Text className="version-share-label">过期时间</Text>
          <DatePicker
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            value={expiresAt}
            onChange={handleExpiryChange}
            disabledDate={(current) => current && current < dayjs().startOf('day')}
            disabledTime={disabledExpiryTime}
            className="version-share-expiry"
          />
        </div>
        {shareLink && (
          <div className="version-share-field">
            {generatedExpiresAt && (
              <Text type="secondary">
                有效至 {dayjs(generatedExpiresAt).format('YYYY-MM-DD HH:mm')}
              </Text>
            )}
            <Text className="version-share-label">分享下载链接</Text>
            <div className="version-share-link-row">
              <Input
                ref={shareLinkInputRef}
                value={shareLink}
                readOnly
                onFocus={(event) => event.target.select()}
              />
              <Button icon={<CopyOutlined />} onClick={handleCopy}>复制</Button>
              <Button icon={<DownloadOutlined />} href={shareLink} target="_blank" rel="noreferrer">
                测试下载
              </Button>
            </div>
          </div>
        )}
      </Space>
    </Modal>
  );
}
